import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import client, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import "@/styles/app.css";

const PENDING_KEY = "criteri_pending_analysis";

const PHASES = [
    { label: "Tú", start: 0, len: 7 },
    { label: "Tu piel", start: 7, len: 3 },
    { label: "Tu rutina", start: 10, len: 2 },
];
const phaseFill = (step, { start, len }) => {
    if (step >= start + len) return 100;
    if (step < start) return 0;
    return Math.round(((step - start) / len) * 100);
};

const OPT = {
    intencion: [["no_se", "No sé qué productos me convienen"], ["combinar", "Tengo productos, pero no sé cómo combinarlos"],
        ["no_funciona", "Mi rutina no me está funcionando"], ["desde_cero", "Quiero empezar desde cero"],
        ["comprar_mejor", "Quiero comprar mejor y gastar menos"], ["otro", "Otro"]],
    detonante: [["comprado", "He comprado productos y no sé si he acertado"], ["cambio", "Mi piel ha cambiado o reacciona de otra manera"],
        ["sin_resultados", "Llevo tiempo sin notar resultados"], ["sin_usar", "Tengo productos que no sé cómo utilizar"],
        ["reorganizar", "Quiero empezar o reorganizar mi rutina"], ["comprobar", "Solo quiero comprobar si voy por buen camino"], ["otro", "Otro"]],
    consecuencia: [["dinero", "Gastar dinero y volver a equivocarme"], ["reaccion", "Provocar una reacción o empeorar mi piel"],
        ["duplicados", "Tener productos sin usar o duplicados"], ["sin_resultados", "No conseguir resultados"],
        ["tiempo", "Perder tiempo con información contradictoria"], ["abrumada", "Sentirme abrumada y posponer la decisión"],
        ["aprender", "No me preocupa; quiero aprender"], ["otro", "Otro"]],
    solucion_actual: [["redes", "Busco en redes sociales o vídeos"], ["tienda", "Pregunto en una tienda o farmacia"],
        ["profesional", "Consulto a una profesional o alguien de confianza"], ["ia", "Uso ChatGPT u otra IA"],
        ["pruebo", "Compro y pruebo hasta encontrar algo"], ["dejo", "Dejo de utilizar el producto"],
        ["pospongo", "Lo pospongo porque no sé qué decidir"], ["otro", "Otro"]],
    rutina_actual: [["casi_nada", "No uso casi nada"], ["basicos", "Uso dos o tres básicos"],
        ["completa", "Tengo una rutina bastante completa"], ["muchos", "Tengo muchos productos y poca claridad"],
        ["cambio", "Voy cambiando según lo que veo o me recomiendan"]],
    edad_rango: [["18-24", "18–24"], ["25-34", "25–34"], ["35-44", "35–44"], ["45-54", "45–54"], ["55-64", "55–64"], ["65", "65+"]],
    identidad: [["mujer", "Mujer"], ["hombre", "Hombre"], ["otra", "Otra identidad / no binaria"], ["ns", "Prefiero no decirlo"]],
    context: [["embarazo", "Embarazo"], ["lactancia", "Lactancia"], ["menopausia", "Perimenopausia o menopausia"],
        ["hormonal", "Tratamiento hormonal"], ["medicacion", "Medicación que afecta a mi piel"],
        ["alergia", "Alergia o sensibilidad conocida"], ["patologia", "Patología cutánea diagnosticada"],
        ["nada", "Nada de lo anterior"], ["ns", "Prefiero no decirlo"]],
    comportamiento: [["tirante", "Tirante o seca"], ["comoda", "Cómoda y equilibrada"], ["brillo_zonas", "Con brillo en algunas zonas"],
        ["brillo_todo", "Con brillo en casi todo el rostro"], ["cambia", "Cambia según el día o la zona"],
        ["irrita", "Se irrita o enrojece con facilidad"], ["no_se", "No lo sé"]],
    objetivos: [["hidratacion", "Hidratación y confort"], ["manchas", "Manchas o tono desigual"], ["granitos", "Granitos e imperfecciones"],
        ["poros", "Poros y textura"], ["lineas", "Líneas y firmeza"], ["rojeces", "Rojeces o sensibilidad"],
        ["brillo", "Brillo"], ["luminosidad", "Luminosidad"], ["prevenir", "Prevenir y mantener"], ["no_claro", "Todavía no lo tengo claro"]],
    tolerancia: [["bien", "Normalmente lo tolero bien"], ["aveces", "A veces noto picor o tirantez"],
        ["irrito", "Me irrito o enrojezco con facilidad"], ["reacciones", "He tenido reacciones importantes"],
        ["no_pruebo", "No suelo probar productos nuevos"], ["no_se", "No lo sé"]],
    complejidad: [["sencilla", "Rutina sencilla"], ["media", "Puedo seguir 3–4 pasos"], ["completa", "Disfruto de una rutina completa"]],
    constancia: [["constante", "Soy constante"], ["cuesta", "Me cuesta mantenerla"]],
    fotoproteccion: [["casi_siempre", "Casi todos los días"], ["aveces", "Solo a veces o nunca"]],
};

// Frases naturales (2ª persona) para la primera devolución.
// Solo se usan datos declarados; lo desconocido ("No lo sé") se omite, nunca se inventa.
const SAY_PIEL = {
    tirante: "tiende a notarse tirante o seca",
    comoda: "suele estar cómoda y equilibrada",
    brillo_zonas: "brilla en algunas zonas",
    brillo_todo: "brilla en casi todo el rostro",
    cambia: "cambia según el día o la zona",
    irrita: "se irrita o enrojece con facilidad",
};
const SAY_TOL_FUSED = {
    bien: "sueles tolerar bien los productos nuevos",
    aveces: "a veces notas picor o tirantez al probar algo nuevo",
    irrito: "te irritas o enrojeces cuando pruebas algo nuevo",
    reacciones: "has tenido reacciones importantes con algún producto",
    no_pruebo: "prefieres no probar productos nuevos",
};
const SAY_OBJETIVO = {
    hidratacion: "la hidratación y el confort",
    manchas: "las manchas o el tono desigual",
    granitos: "los granitos e imperfecciones",
    poros: "los poros y la textura",
    lineas: "las líneas y la firmeza",
    rojeces: "las rojeces o la sensibilidad",
    brillo: "el brillo",
    luminosidad: "la luminosidad",
    prevenir: "prevenir y mantener",
};
const SAY_RUTINA = {
    sencilla: "buscas una rutina sencilla",
    media: "te va bien una rutina de tres o cuatro pasos",
    completa: "disfrutas de una rutina completa",
};
const SAY_CONTEXT = {
    embarazo: "estás embarazada",
    lactancia: "estás en período de lactancia",
    menopausia: "estás en perimenopausia o menopausia",
    hormonal: "sigues un tratamiento hormonal",
    medicacion: "tomas medicación que afecta a tu piel",
    alergia: "tienes una alergia o sensibilidad conocida",
    patologia: "tienes una patología cutánea diagnosticada",
};

const joinNatural = (arr) => (arr.length <= 1 ? arr.join("") : arr.slice(0, -1).join(", ") + " y " + arr[arr.length - 1]);

function devolucionSentences(a) {
    const out = [];

    // 1) Preocupación principal: prioridad declarada o, en su defecto, los objetivos elegidos.
    const metas = a.prioridad && SAY_OBJETIVO[a.prioridad]
        ? [SAY_OBJETIVO[a.prioridad]]
        : (a.objetivos || []).filter((o) => SAY_OBJETIVO[o]).map((o) => SAY_OBJETIVO[o]);
    if (metas.length) {
        out.push(`Entendemos que lo que más te preocupa tiene que ver con ${joinNatural(metas)}.`);
    } else {
        out.push("Todavía no tienes claro qué priorizar, y no pasa nada: empezaremos por ordenar lo que ya usas.");
    }

    // 2) Cómo es tu piel + qué pasa al probar algo nuevo (fusionado, sin repetir) + tipo de rutina.
    const clauses = [];
    const pielSensible = a.comportamiento === "irrita";
    const tolSensible = a.tolerancia === "irrito" || a.tolerancia === "reacciones";
    if (SAY_PIEL[a.comportamiento]) {
        let piel = `tu piel ${SAY_PIEL[a.comportamiento]}`;
        if (pielSensible && tolSensible) piel += ", especialmente cuando introduces productos nuevos";
        clauses.push(piel);
    }
    if (!(pielSensible && tolSensible) && SAY_TOL_FUSED[a.tolerancia]) {
        clauses.push(SAY_TOL_FUSED[a.tolerancia]);
    }
    if (a.habitos && SAY_RUTINA[a.habitos.complejidad]) {
        clauses.push(SAY_RUTINA[a.habitos.complejidad]);
    }
    if (clauses.length) out.push(`Nos cuentas que ${joinNatural(clauses)}.`);

    // 3) Contexto relevante declarado (la patología va aparte, en su propio aviso).
    const otherCtx = (a.context || []).filter((c) => c !== "patologia" && SAY_CONTEXT[c]).map((c) => SAY_CONTEXT[c]);
    if (otherCtx.length) out.push(`Tendremos en cuenta que ${joinNatural(otherCtx)}.`);

    // 4) Patología diagnosticada: nombre concreto si lo conocemos; si no, fórmula genérica.
    if ((a.context || []).includes("patologia")) {
        const nombrePat = (a.patologia_detalle || "").trim();
        out.push(nombrePat
            ? `Además, nos has indicado que tienes ${nombrePat} diagnosticada.`
            : "Además, nos has indicado que tienes una patología cutánea diagnosticada.");
    }
    return out;
}

function medicalNotice(a) {
    if (!(a.context || []).includes("patologia")) return null;
    const nombrePat = (a.patologia_detalle || "").trim();
    const etiqueta = nombrePat || "una patología cutánea";
    return `Al habernos indicado que tienes ${etiqueta} diagnosticada, las decisiones relacionadas con esta condición deben revisarse con un dermatólogo u otro profesional sanitario. MYAVEREN puede ayudarte a revisar y ordenar cosméticamente los productos que utilizas, pero no diagnostica ni trata patologías. Puedes continuar con tu análisis y utilizar nuestro informe como apoyo para esa consulta.`;
}

function Single({ options, value, onChange, testPrefix }) {
    return (
        <div className={`opts ${options.length <= 3 ? "one" : ""}`}>
            {options.map(([v, l]) => (
                <button key={v} role="radio" aria-checked={value === v} onClick={() => onChange(v)} data-testid={`${testPrefix}-${v}`} className="opt">
                    <span className="mark" />
                    <b>{l}</b>
                </button>
            ))}
        </div>
    );
}

function Multi({ options, value, onToggle, testPrefix }) {
    return (
        <div className="chips">
            {options.map(([v, l]) => (
                <button key={v} aria-pressed={value.includes(v)} onClick={() => onToggle(v)} data-testid={`${testPrefix}-${v}`} className="pill">
                    {l}
                </button>
            ))}
        </div>
    );
}

export default function AnalysisNew() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const token = useRef(crypto.randomUUID());
    const [step, setStep] = useState(0);
    const [a, setA] = useState({
        intencion: "", detonante: "", consecuencia: [], solucion_actual: "", solucion_falta: "",
        rutina_actual: "", edad_rango: "", identidad: "", context: [], alergia_detalle: "", patologia_detalle: "", evitar: "",
        comportamiento: "", objetivos: [], prioridad: "", tolerancia: "",
        habitos: { complejidad: "", constancia: "", fotoproteccion: "" },
    });
    const [loading, setLoading] = useState(false);
    const set = (k, v) => setA((p) => ({ ...p, [k]: v }));
    const toggle = (k, v, max) => setA((p) => {
        const has = p[k].includes(v);
        if (has) return { ...p, [k]: p[k].filter((x) => x !== v) };
        if (max && p[k].length >= max) return p;
        return { ...p, [k]: [...p[k], v] };
    });
    const setHab = (k, v) => setA((p) => ({ ...p, habitos: { ...p.habitos, [k]: v } }));

    const STEPS = [
        { id: "intencion", title: "¿Qué te ha traído hasta MYAVEREN?", ok: () => a.intencion },
        { id: "detonante", title: "¿Qué ha ocurrido para que hoy revises tu rutina?", ok: () => a.detonante },
        { id: "consecuencia", title: "¿Qué es lo que más te preocupa? Elige hasta dos.", ok: () => a.consecuencia.length > 0, max: 2 },
        { id: "solucion", title: "Cuando tienes una duda, ¿qué haces normalmente?", ok: () => a.solucion_actual },
        { id: "rutina_actual", title: "¿Cómo es tu rutina ahora mismo?", ok: () => a.rutina_actual },
        { id: "datos", title: "Unos datos básicos", ok: () => a.edad_rango },
        { id: "context", title: "¿Hay algo que debamos tener en cuenta?", ok: () => a.context.length > 0 },
        { id: "comportamiento", title: "En un día normal, ¿cómo suele sentirse tu piel?", ok: () => a.comportamiento },
        { id: "objetivos", title: "¿Qué te gustaría cuidar ahora? Elige hasta tres.", ok: () => a.objetivos.length > 0 && (a.objetivos.includes("no_claro") || a.prioridad), max: 3 },
        { id: "tolerancia", title: "Cuando pruebas algo nuevo, ¿qué suele pasar?", ok: () => a.tolerancia },
        { id: "habitos", title: "Tus hábitos", ok: () => a.habitos.complejidad && a.habitos.constancia && a.habitos.fotoproteccion },
        { id: "devolucion", title: "Esto es lo que estamos entendiendo de ti", ok: () => true },
    ];
    const cur = STEPS[step];

    const submit = async () => {
        const payload = { ...a, client_token: token.current };
        const authed = !!user && user !== false;
        // Usuaria anónima: guardamos las respuestas (sin crear nada) y la llevamos al registro.
        if (!authed) {
            sessionStorage.setItem(PENDING_KEY, JSON.stringify(payload));
            navigate("/crear-cuenta");
            return;
        }
        setLoading(true);
        try {
            await client.post("/analyses", payload);
            toast.success("Hecho. Vamos a cruzarlo con tu neceser.");
            navigate("/app/neceser", { replace: true });
        } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
    };

    const priorityOptions = a.objetivos.filter((o) => o !== "no_claro")
        .map((o) => OPT.objetivos.find(([v]) => v === o));

    return (
        <div className="criteri-test mx-auto max-w-3xl">
            <p className="eyebrow">Test de piel · {STEPS.length} pasos · unos 3 minutos</p>
            <div className="prog mt-4 mb-8">
                {PHASES.map((p) => (
                    <div key={p.label}>
                        <i style={{ "--p": `${phaseFill(step, p)}%` }} />
                        {p.label}
                    </div>
                ))}
            </div>

            <div data-testid={`test-step-${cur.id}`}>
                <p className="eyebrow">Pregunta {step + 1} de {STEPS.length}</p>
                <h2 className="q">{cur.title}</h2>
                <div className="mt-5">
                    {cur.id === "intencion" && <Single options={OPT.intencion} value={a.intencion} onChange={(v) => set("intencion", v)} testPrefix="intencion" />}
                    {cur.id === "detonante" && <Single options={OPT.detonante} value={a.detonante} onChange={(v) => set("detonante", v)} testPrefix="detonante" />}
                    {cur.id === "consecuencia" && (<>
                        <p className="mb-3 text-[13px] text-subtle">{a.consecuencia.length}/2 seleccionadas</p>
                        <Multi options={OPT.consecuencia} value={a.consecuencia} onToggle={(v) => toggle("consecuencia", v, 2)} testPrefix="consecuencia" />
                    </>)}
                    {cur.id === "solucion" && (<>
                        <Single options={OPT.solucion_actual} value={a.solucion_actual} onChange={(v) => set("solucion_actual", v)} testPrefix="solucion" />
                        <div className="mt-4 space-y-2">
                            <Label htmlFor="falta">¿Qué le falta a esa forma de resolverlo? (opcional)</Label>
                            <Input id="falta" value={a.solucion_falta} onChange={(e) => set("solucion_falta", e.target.value)} data-testid="solucion-falta" />
                        </div>
                    </>)}
                    {cur.id === "rutina_actual" && <Single options={OPT.rutina_actual} value={a.rutina_actual} onChange={(v) => set("rutina_actual", v)} testPrefix="rutina" />}
                    {cur.id === "datos" && (<div className="space-y-6">
                        <div><Label className="mb-2.5 block">Tu edad</Label><Single options={OPT.edad_rango} value={a.edad_rango} onChange={(v) => set("edad_rango", v)} testPrefix="edad" /></div>
                        <div><Label className="mb-2.5 block">Identidad (opcional, solo para el lenguaje)</Label><Single options={OPT.identidad} value={a.identidad} onChange={(v) => set("identidad", v)} testPrefix="identidad" /></div>
                    </div>)}
                    {cur.id === "context" && (<>
                        <Multi options={OPT.context} value={a.context} onToggle={(v) => toggle("context", v)} testPrefix="context" />
                        {a.context.includes("alergia") && (
                            <div className="mt-4 space-y-2">
                                <Label htmlFor="al">¿A qué ingrediente y qué reacción? (opcional)</Label>
                                <Input id="al" value={a.alergia_detalle} onChange={(e) => set("alergia_detalle", e.target.value)} data-testid="alergia-detalle" />
                            </div>)}
                        {a.context.includes("patologia") && (
                            <div className="mt-4 space-y-2">
                                <Label htmlFor="pat">¿Qué patología te han diagnosticado? (opcional)</Label>
                                <Input id="pat" value={a.patologia_detalle} onChange={(e) => set("patologia_detalle", e.target.value)} data-testid="patologia-detalle" />
                            </div>)}
                        <div className="mt-4 space-y-2">
                            <Label htmlFor="ev">¿Alguna marca o ingrediente que quieras evitar? (opcional)</Label>
                            <Input id="ev" value={a.evitar} onChange={(e) => set("evitar", e.target.value)} data-testid="evitar" />
                        </div>
                    </>)}
                    {cur.id === "comportamiento" && <Single options={OPT.comportamiento} value={a.comportamiento} onChange={(v) => set("comportamiento", v)} testPrefix="comportamiento" />}
                    {cur.id === "objetivos" && (<>
                        <p className="mb-3 text-[13px] text-subtle">{a.objetivos.length}/3 seleccionados</p>
                        <Multi options={OPT.objetivos} value={a.objetivos} onToggle={(v) => toggle("objetivos", v, 3)} testPrefix="objetivos" />
                        {priorityOptions.length > 0 && (
                            <div className="mt-6">
                                <Label className="mb-2.5 block">¿Cuál es tu prioridad número uno?</Label>
                                <Single options={priorityOptions} value={a.prioridad} onChange={(v) => set("prioridad", v)} testPrefix="prioridad" />
                            </div>)}
                    </>)}
                    {cur.id === "tolerancia" && <Single options={OPT.tolerancia} value={a.tolerancia} onChange={(v) => set("tolerancia", v)} testPrefix="tolerancia" />}
                    {cur.id === "habitos" && (<div className="space-y-6">
                        <div><Label className="mb-2.5 block">Complejidad</Label><Single options={OPT.complejidad} value={a.habitos.complejidad} onChange={(v) => setHab("complejidad", v)} testPrefix="complejidad" /></div>
                        <div><Label className="mb-2.5 block">Constancia</Label><Single options={OPT.constancia} value={a.habitos.constancia} onChange={(v) => setHab("constancia", v)} testPrefix="constancia" /></div>
                        <div><Label className="mb-2.5 block">Fotoprotección</Label><Single options={OPT.fotoproteccion} value={a.habitos.fotoproteccion} onChange={(v) => setHab("fotoproteccion", v)} testPrefix="fotoproteccion" /></div>
                    </div>)}
                    {cur.id === "devolucion" && (
                        <>
                            <div className="devcard" data-testid="first-devolucion">
                                <p data-testid="devolucion-summary">{devolucionSentences(a).join(" ")}</p>
                                <p className="mt-5"><strong>¿Lo hemos entendido bien?</strong></p>
                                <p className="mt-1 text-[14px]" style={{ color: "var(--gris)" }}>Si algo no encaja, puedes volver atrás y corregirlo.</p>
                            </div>
                            {medicalNotice(a) && (
                                <div className="mt-4 rounded-lg border border-terracotta/30 bg-terracotta/5 p-4" data-testid="medical-notice">
                                    <p className="text-[13px] leading-[1.6]" style={{ color: "var(--gris)" }}>{medicalNotice(a)}</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <div className="mt-8 flex justify-between">
                <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid={step === STEPS.length - 1 ? "devolucion-correct" : undefined}>
                    {step === STEPS.length - 1 ? "Quiero corregir algo" : "Atrás"}
                </Button>
                {step < STEPS.length - 1 ? (
                    <Button disabled={!cur.ok()} onClick={() => setStep(step + 1)} data-testid="test-next">Continuar</Button>
                ) : (
                    <Button disabled={loading} onClick={submit} data-testid="test-submit">
                        {loading ? "Guardando…" : "Sí, continuar"}
                    </Button>
                )}
            </div>
        </div>
    );
}
